import { apiClient } from '../../core/api';
import { normalizeError } from '../../utils/errorHandling';
import type {
  CreditHistoryItem,
  CreditHistoryType,
  CreditOverview,
  CreditPlan,
  CreditPrecheck,
} from '../../../types/credit';

/**
 * Digital Staff credit-billing API (chatbot-api `/v1/credits/*`,
 * `/v1/billing/credit-plans`). The chatbot base URL already carries the
 * `/chatbot/api/v1` prefix, so endpoints are written without it.
 */
class CreditApiClient {
  /** Current pool balance / usage + per-agent split. */
  async getOverview(userId: string): Promise<CreditOverview> {
    try {
      const res = await apiClient.chatbotGet<CreditOverview>(`/credits/overview/${userId}`);
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  }

  /** Credit usage / addition history. */
  async getHistory(
    userId: string,
    type?: CreditHistoryType,
    limit = 50
  ): Promise<CreditHistoryItem[]> {
    try {
      const params: Record<string, unknown> = { limit };
      if (type) params.type = type;
      const res = await apiClient.chatbotGet<CreditHistoryItem[]>(
        `/credits/history/${userId}`,
        params
      );
      return res.data ?? [];
    } catch (error) {
      throw normalizeError(error);
    }
  }

  /** Pre-task gate. For known-cost tasks pass estimatedCredits; conversations omit it. */
  async precheck(
    userId: string,
    opts?: { estimatedCredits?: number; kind?: 'conversation' | 'task' }
  ): Promise<CreditPrecheck> {
    try {
      const params: Record<string, unknown> = {};
      if (opts?.estimatedCredits != null) params.estimatedCredits = opts.estimatedCredits;
      if (opts?.kind) params.kind = opts.kind;
      const res = await apiClient.chatbotGet<CreditPrecheck>(`/credits/precheck/${userId}`, params);
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  }

  /**
   * Add-on credits request (no self-serve top-up in Phase 1): emails the support
   * inbox via the credit-top-up template with the requested amount.
   */
  async contactSupport(userId: string, creditsRequested: number): Promise<void> {
    try {
      await apiClient.chatbotPost(
        `/credits/contact-support/${userId}`,
        {},
        { params: { creditsRequested } }
      );
    } catch (error) {
      throw normalizeError(error);
    }
  }

  /**
   * Redeem the second free-trial bucket (+500) when the first runs out. Idempotent
   * and per-store-capped server-side (one redeem; 1,000 lifetime cap). Returns the
   * refreshed overview with the topped-up balance and `trialRedeemAvailable=false`.
   */
  async redeemTrialCredits(userId: string): Promise<CreditOverview> {
    try {
      const res = await apiClient.chatbotPost<CreditOverview>(`/credits/redeem-trial/${userId}`, {});
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  }

  /** Catalog of credit plans for the AI Team / billing UI. */
  async getCreditPlans(): Promise<CreditPlan[]> {
    try {
      const res = await apiClient.chatbotGet<CreditPlan[]>(`/billing/credit-plans`);
      return res.data ?? [];
    } catch (error) {
      throw normalizeError(error);
    }
  }
}

export const creditApi = new CreditApiClient();
